import { useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import {
  FormTitleBox,
  ConfirmationDialog,
  TableBodySkeleton,
} from '../../../common/components';
import ProjectsDataTable from './components/ProjectsDataTable';
import ProjectsPagination from './components/ProjectsPagination';
import ProjectDialog from './components/ProjectDialog';
import ProjectProgressDialog from './components/ProjectProgressDialog';
import ProjectQualityDialog from './components/ProjectQualityDialog';
import ProjectsFilters from './components/ProjectsFilters';
import DashboardProgressImportDialog from './components/DashboardProgressImportDialog';
import { useProjectManagement } from './hooks/useProjectManagement';
import AddIcon from '@mui/icons-material/Add';

/**
 * Dashboard de gestión de proyectos
 * Migrado desde DocAssemble siguiendo los patrones del proyecto
 */
const ProjectManagementDashboard = () => {
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const {
    // Data principal
    projects,
    allProjects,
    lots,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    filters,

    // Estados de diálogos
    dialogOpen,
    progressDialogOpen,
    qualityDialogOpen,
    editMode,
    selectedProject,

    // Datos de soporte
    subscribers,
    allotments,
    subcontractors,
    progressTemplates,
    qualityTemplates,

    // Filtros
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,

    // Funciones de manejo
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleCloseProgressDialog,
    handleCloseQualityDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleStatusChange,
    handleConstructionTypeChange,
    handleViewProgress,
    handleViewQuality,

    // Diálogos de confirmación
    deleteConfirmOpen,
    projectToDelete,
    handleConfirmDelete,
    handleCancelDelete,

    // Notificaciones
    snackbar,
    handleCloseSnackbar,
  } = useProjectManagement();

  return (
    <Box>
      <FormTitleBox
        title="Gestión de Proyectos | Proyectos"
        showButton={true}
        buttonText="Nuevo Proyecto"
        buttonIcon={AddIcon}
        onButtonClick={handleOpenDialog}
        showSearch={true}
        searchLabel="Buscar proyecto"
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchSize="medium"
      />

      <ProjectsFilters
        filters={filters}
        allotments={allotments}
        subcontractors={subcontractors}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onOpenImportDialog={() => setImportDialogOpen(true)}
      />

      <ProjectsDataTable
        data={projects}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        onConstructionTypeChange={handleConstructionTypeChange}
        onViewProgress={handleViewProgress}
        onViewQuality={handleViewQuality}
        subscribers={subscribers}
        LoadingComponent={TableBodySkeleton}
      />

      <ProjectsPagination
        totalPages={totalPages}
        totalCount={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <ProjectDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editMode={editMode}
        selectedProject={selectedProject}
        onInputChange={handleInputChange}
        onSelectChange={handleSelectChange}
        onSave={handleSave}
        subscribers={subscribers}
        progressTemplates={progressTemplates}
        qualityTemplates={qualityTemplates}
      />
      <ProjectProgressDialog
        open={progressDialogOpen}
        onClose={handleCloseProgressDialog}
        project={selectedProject}
        progressTemplates={progressTemplates}
      />

      <ProjectQualityDialog
        open={qualityDialogOpen}
        onClose={handleCloseQualityDialog}
        project={selectedProject}
        qualityTemplates={qualityTemplates}
      />

      <DashboardProgressImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        projects={allProjects}
        lots={lots}
        allotments={allotments}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        entityName="proyecto"
        message={`¿Está seguro que quiere eliminar el proyecto "${projectToDelete?.name}" de forma permanente?`}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectManagementDashboard;
